import React, { useState, useCallback, useMemo } from 'react';
import { Collection, Button } from '@components/common';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import { useUserCapabilities } from '@hooks/useAuthorization';
import Modal from '@components/common/Modal';
import TagForm from './TagForm';
import Icon from '@components/Icon/Icon';
import api from '@services/api';
import './TagsPage.scss';

const TagsPage = () => {
  const { t } = useLanguage();
  const notify = useNotification();
  const [showFormModal, setShowFormModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { capabilities } = useUserCapabilities();

  const handleCreateTag = async (tagData) => {
    try {
      await api.post('/tags', tagData);
      setShowFormModal(false);
      setRefreshTrigger(prev => prev + 1);
      notify.success(t('tagsAdmin.createSuccess'));
    } catch (err) {
      console.error(t('tagsAdmin.createError'), err);
      notify.error(t('tagsAdmin.createError'));
      throw err;
    }
  };

  const openCreateModal = useCallback(() => {
    setShowFormModal(true);
  }, []);

  const closeFormModal = useCallback(() => {
    setShowFormModal(false);
  }, []);

  const customHeader = useMemo(() => {
    // Only show create button if user has admin capabilities (tags might require admin permissions)
    if (!capabilities?.capabilities?.can_admin) {
      return null;
    }

    return (
      <Button
        variant="primary"
        size="medium"
        onClick={openCreateModal}
        className="create-tag-button"
      >
        <Icon icon="plus" size="sm" />
        {t('tagsAdmin.createTag')}
      </Button>
    );
  }, [t, openCreateModal, capabilities]);

  return (
    <div className="tags-page">
      <Collection
        modelName="tags"
        displayType="list"
        title={t('tags.title')}
        description={t('tags.subtitle')}
        customHeader={customHeader}
        className="tags-collection"
        refreshTrigger={refreshTrigger}
        enableSearch={true}
        enablePagination={true}
        onRefresh={() => setRefreshTrigger(prev => prev + 1)}
      />

      <Modal
        isOpen={showFormModal}
        onClose={closeFormModal}
        title={t('tagsAdmin.createTag')}
        className="tag-form-modal"
      >
        <TagForm
          tag={null}
          onSubmit={handleCreateTag}
          onCancel={closeFormModal}
        />
      </Modal>
    </div>
  );
};

export default TagsPage;
